import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from 'hooks/useTranslation';
import { TRANSLATION_KEYS } from '../../constants';
import LastResults from './LastResults';
import FeaturedPlayerCard from './FeaturedPlayerCard';
import styles from './CountryHeader.module.scss';
import PlaceholderEscudo from 'containers/Mundial2026/assets/placeholders/Escudo.png';
import PlaceholderBandera from 'containers/Mundial2026/assets/placeholders/Bandera.png';

const FALLBACK_COLOR = '#f77f00';
const EMPTY_VALUE = '—';

const ChevronLeftIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="16"
    viewBox="0 0 10 16"
    fill="none"
  >
    <path
      d="M2.70532 7.99747L8.99156 1.90238C9.20587 1.69512 9.31948 1.43848 9.33239 1.13245C9.3453 0.826425 9.23169 0.557309 8.99156 0.325109C8.76693 0.108371 8.49479 2.82437e-06 8.17514 2.81039e-06C7.85549 2.79642e-06 7.58335 0.108371 7.35871 0.325108L0.396248 7.05058C0.251398 7.19075 0.14915 7.33853 0.0895065 7.49391C0.0298618 7.64929 4.0412e-05 7.81714 4.04041e-05 7.99747C4.03962e-05 8.17779 0.0298618 8.34564 0.0895065 8.50102C0.14915 8.65641 0.251398 8.80418 0.396248 8.94435L7.35871 15.6698C7.57328 15.8768 7.83896 15.9866 8.15577 15.999C8.47258 16.0115 8.75118 15.9018 8.99156 15.6698C9.21594 15.4528 9.32813 15.19 9.32813 14.8812C9.32813 14.5724 9.21594 14.3095 8.99156 14.0926L2.70532 7.99747Z"
      fill="white"
    />
  </svg>
);

const renderNumber = value =>
  value === null || value === undefined ? EMPTY_VALUE : value;

const CountryHeader = ({
  team = {},
  recentResults = [],
  featuredPlayers = [],
  summary = {},
  isFavorite,
  onBack,
  onToggleFavorite,
  topLayerRef,
}) => {
  const { translationMapper } = useTranslation();
  const translations = translationMapper(TRANSLATION_KEYS);
  const { name = '', crestUrl, flagUrl, primaryHex } = team;
  const [crestLoadFailed, setCrestLoadFailed] = React.useState(false);

  React.useEffect(() => {
    setCrestLoadFailed(false);
  }, [crestUrl]);

  const color = primaryHex || FALLBACK_COLOR;
  const assets = useSelector(state => state.asset);
  const favoriteText = isFavorite
    ? translations.ADD_TO_FAVORITES
    : translations.REMOVE_FROM_FAVORITES;

  return (
    <section
      className={styles['country-header']}
      style={{ '--wc-ch-color': color }}
    >
      <div className={styles['country-header__top-line']} aria-hidden />

      <div className={styles['country-header__inner']}>
        <button
          type="button"
          className={styles['country-header__back']}
          onClick={onBack}
          aria-label={translations.BACK_BUTTON}
        >
          <span className={styles['country-header__back-icon-wrapper']}>
            <ChevronLeftIcon className={styles['country-header__back-icon']} />
          </span>
        </button>

        <div ref={topLayerRef} className={styles['country-header__top-layer']}>
          <div className={styles['country-header__top']}>
            <div className={styles['country-header__country']}>
              <div className={styles['country-header__country-identity']}>
                <div className={styles['country-header__crest-row']}>
                  <div className={classNames(styles['country-header__crest-ring'], {
                    [styles['country-header__crest-ring--no-bg']]: crestLoadFailed,
                  })}>
                    {crestUrl ? (
                      <img
                        className={styles['country-header__crest-image']}
                        src={crestUrl}
                        alt=""
                        onError={e => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PlaceholderEscudo;
                          setCrestLoadFailed(true);
                        }}
                      />
                    ) : (
                      <span
                        className={styles['country-header__crest-fallback']}
                      >
                        {(name || '').slice(0, 3).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {flagUrl && (
                    <span className={styles['country-header__flag']}>
                      <img
                        className={styles['country-header__flag-image']}
                        src={flagUrl}
                        alt=""
                        onError={e => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PlaceholderBandera;
                        }}
                      />
                    </span>
                  )}
                </div>

                <h2 className={styles['country-header__name']}>{name}</h2>
              </div>

              <LastResults items={recentResults} />
            </div>

            {(summary?.matches != null || summary.points != null) && (
              <div className={styles['country-header__stats']}>
                <div className={styles['country-header__stat']}>
                  <p className={styles['country-header__stat-label']}>
                    {translations.MATCHES}
                  </p>
                  <p className={styles['country-header__stat-value']}>
                    {renderNumber(summary.matches)}
                  </p>
                </div>
                <div className={styles['country-header__stat']}>
                  <p className={styles['country-header__stat-label']}>
                    {translations.POINTS}
                  </p>
                  <p className={styles['country-header__stat-value']}>
                    {renderNumber(summary.points)}
                  </p>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className={classNames(styles['country-header__favorite'], {
              [styles['country-header__favorite--active']]: isFavorite,
            })}
            onClick={onToggleFavorite}
            aria-label={favoriteText}
            title={favoriteText}
          >
            <img
              className={styles['country-header__favorite-icon']}
              src={
                isFavorite
                  ? assets?.eventodeportivo_icono_agregarfavorito
                  : assets?.eventodeportivo_icono_quitarfavorito
              }
              width={16}
              height={16}
              alt=""
            />
            <span>{favoriteText}</span>
          </button>
        </div>

        {featuredPlayers?.length > 0 && (
          <div className={styles['country-header__bottom-layer']}>
            <div className={styles['country-header__featured']}>
              <p className={styles['country-header__featured-title']}>
                {translations.FEATURED_PLAYERS}
              </p>
              <div className={styles['country-header__featured-list']}>
                {featuredPlayers.map(item => (
                  <FeaturedPlayerCard
                    key={
                      item?.player?.playerId ||
                      `${item?.category}-${item?.player?.name}`
                    }
                    headline={item?.headline}
                    player={item?.player}
                    color={color}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <span
          className={classNames(
            styles['country-header__divider'],
            styles['country-header__divider--left'],
          )}
          aria-hidden
        />
        <span
          className={classNames(
            styles['country-header__divider'],
            styles['country-header__divider--right'],
          )}
          aria-hidden
        />
      </div>
    </section>
  );
};

export default CountryHeader;
